export interface PosthogConfig {
  key: string
  host: string
  /** Reverse-proxy host for feature-flag requests; unset means flags go to `host`. */
  flagsHost?: string
}

export interface PosthogKeys {
  /** VITE_POSTHOG_KEY_US */
  us: string | undefined
  /** VITE_POSTHOG_KEY_UK */
  uk: string | undefined
}

/** Must stay in sync with web-app's `Environment` enum values. */
export type PosthogEnvironment =
  'local' | 'development' | 'staging' | 'production' | 'production_uk'

const US_HOST = 'https://us.i.posthog.com'
const EU_HOST = 'https://eu.i.posthog.com'

const UK_ENVIRONMENT: PosthogEnvironment = 'production_uk'

// First-party reverse proxy so ad blockers can't block flag evaluation; event
// capture still goes direct to PostHog Cloud. The UK entry is inert until a UK
// project and key exist. An unlisted environment gets no proxy, so `satisfies`
// guards against a mistyped key.
const FLAGS_PROXY_HOSTS = {
  production: 'https://feature.mangovoice.com',
  [UK_ENVIRONMENT]: 'https://feature.mangovoice.co.uk',
  staging: 'https://feature-staging.mangovoice.com',
} satisfies Partial<Record<PosthogEnvironment, string>> as Record<
  string,
  string | undefined
>

// PostHog keys always start with phc_; anything else counts as unset. Amplify
// env vars can't be blank, so non-prod branches carry a placeholder value.
const getValidKey = (value: string | undefined): string | undefined => {
  const key = value?.trim()
  return key?.startsWith('phc_') ? key : undefined
}

/**
 * Resolves the PostHog `{ key, host }` for the user's region: UK routes to a
 * separate EU Cloud project for GDPR data residency. Returns `undefined` when
 * the region has no key — the caller skips init (no cross-region fallback).
 *
 * Keys are arguments, not `import.meta.env` reads, so each consuming app's own
 * build inlines them.
 */
export const getPosthogConfig = (
  environment: string,
  keys: PosthogKeys,
): PosthogConfig | undefined => {
  const isUK = environment === UK_ENVIRONMENT

  const key = getValidKey(isUK ? keys.uk : keys.us)
  if (!key) {
    return undefined
  }
  return {
    key,
    host: isUK ? EU_HOST : US_HOST,
    flagsHost: FLAGS_PROXY_HOSTS[environment],
  }
}
